import { defineStore } from "pinia";
import type {
  ProductBulkPolicyInput,
  ProductBulkPolicyResult,
  ProductBulkPolicyService,
} from "./product-bulk-policy.js";

export const useProductBulkPolicyStore = defineStore("product-bulk-policy", {
  state: (): {
    service?: ProductBulkPolicyService;
    result?: ProductBulkPolicyResult;
    error?: string;
    submitting: boolean;
    needsRefresh: boolean;
  } => ({ submitting: false, needsRefresh: false }),
  actions: {
    configure(service: ProductBulkPolicyService): void {
      this.service = service;
    },
    async execute(input: ProductBulkPolicyInput, refresh: () => Promise<void>): Promise<void> {
      if (this.submitting || this.needsRefresh) return;
      this.submitting = true;
      delete this.error;
      try {
        if (!this.service) throw new Error("Bulk policy actions are unavailable.");
        this.result = await this.service.run(input);
        this.needsRefresh = true;
        await this.refreshResults(refresh);
      } catch (error: unknown) {
        this.error =
          error instanceof Error ? error.message : "Bulk policy action could not complete.";
      } finally {
        this.submitting = false;
      }
    },
    async refresh(refresh: () => Promise<void>): Promise<void> {
      if (this.submitting) return;
      this.submitting = true;
      try {
        await this.refreshResults(refresh);
      } finally {
        this.submitting = false;
      }
    },
    async refreshResults(refresh: () => Promise<void>): Promise<void> {
      try {
        await refresh();
        this.needsRefresh = false;
        delete this.error;
      } catch {
        this.needsRefresh = true;
        this.error =
          "Changes were processed, but products could not be refreshed. Review the results and refresh before another batch.";
      }
    },
  },
});
